import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import profileImageCache from '../../services/profileImageCache';
import userService from '../../services/userService';
import { typography } from '../../styles/typography';
import { generateShiftTimestamps, getShiftDuration, parseTimeString } from '../../utils/timeUtils';
import { Card, IconButton, IconTextButton, PrimaryButton, SearchBar } from '../ui';
import AssignEmployeeTable from './AssignEmployeeTable';
import { deleteShiftRoster, getAllSites, getShiftRoster, sendShiftData } from './ShiftShedule_Api_service';
import ZoneAssignmentManager from './ZoneAssignmentManager';
import { employeeApi } from './employeeApi';

const ScheduleShift = () => {
  // Log screen view
  useScreenLogging('ScheduleShift', {
    component: 'ScheduleShift',
  });
  const location = useLocation();
  const [shiftType, setShiftType] = useState('morning');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedOfficers, setSelectedOfficers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [allSites, setAllSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAllSitesDropdown, setShowAllSitesDropdown] = useState(false);
  
  // Shift management state
  const [shifts, setShifts] = useState([]);
  const [selectedShiftIndex, setSelectedShiftIndex] = useState(0);
  const [isLoadingShifts, setIsLoadingShifts] = useState(false);
  const [shiftsError, setShiftsError] = useState(null);
  
  // Flag to track when we're switching shifts vs when user is changing time
  const [isSwitchingShift, setIsSwitchingShift] = useState(false);
  
  // Schedule shift state
  const [isScheduling, setIsScheduling] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [scheduleSuccess, setScheduleSuccess] = useState(false);

  // Get overlay functions and data from context
  const { openOverlay, datePickerData, startTimeData, endTimeData, assignedEmployees, setAssignedEmployees, assignedEmployeesToZones, setAssignedEmployeesToZones, setStartTimeData, setEndTimeData, setDatePickerData, overnightShiftInfo } = useOverlay();

  // Clear schedule status when form changes
  useEffect(() => {
    setScheduleError(null);
    setScheduleSuccess(false);
  }, [selectedSite, assignedEmployees, assignedEmployeesToZones, shifts, selectedShiftIndex]);

  // Sync context with current shift data when shifts change
  useEffect(() => {
    if (shifts.length > 0 && selectedShiftIndex < shifts.length) {
      const currentShift = shifts[selectedShiftIndex];
      
      // Update supervisors in context
      if (currentShift.supervisors && currentShift.supervisors.length > 0) {
        setAssignedEmployees(currentShift.supervisors);
      } else if (!assignedEmployees || assignedEmployees.length === 0) {
        setAssignedEmployees([]);
      }
      
      // Update guards in context
      if (currentShift.zoneGuards && Object.keys(currentShift.zoneGuards).length > 0) {
        setAssignedEmployeesToZones(currentShift.zoneGuards);
      } else if (!assignedEmployeesToZones || Object.keys(assignedEmployeesToZones).length === 0) {
        setAssignedEmployeesToZones({});
      }
    }
  }, [shifts, selectedShiftIndex]);

  // Helper function to cache profile images for a user
  const cacheUserProfileImage = async (user) => {
    try {
      const userId = user.user_id || user.employee_id;
      if (!userId) return;
      
      const imagePath = user.image_path || user.image_presigned_url;
      if (!imagePath || imagePath.trim() === '') return;
      
      // Check if already cached
      const cachedUrl = profileImageCache.get(userId);
      if (cachedUrl) {
        return; // Already cached
      }
      
      // If it's already a URL, cache it directly
      if (imagePath.startsWith('http')) {
        profileImageCache.set(userId, imagePath);
        console.log(`💾 Cached direct profile image URL for user ${userId}`);
        return;
      }
      
      // Cache the S3 path first
      profileImageCache.setS3Path(userId, imagePath);
      
      // Try to get presigned URL and cache it (async, don't block)
      userService.getS3PresignedUrl(imagePath)
        .then(presignedUrl => {
          if (presignedUrl) {
            profileImageCache.set(userId, presignedUrl, imagePath);
            console.log(`💾 Cached profile image URL for user ${userId}`);
          }
        })
        .catch(error => {
          console.warn(`⚠️ Failed to cache presigned URL for user ${userId}:`, error);
        });
    } catch (error) {
      console.warn(`⚠️ Failed to cache profile image for user ${user.user_id}:`, error);
    }
  };

  // Function to fetch user details for multiple user IDs using bulk API endpoints
  const fetchUserDetails = async (userIds) => {
    if (!userIds || userIds.length === 0) return {};
    
    try {
      // Fetch all users using the working bulk API endpoints
      const [guardsData, supervisorsData] = await Promise.all([
        employeeApi.fetchAvailableGuards().catch(error => {
          console.warn('Failed to fetch guards:', error);
          return [];
        }),
        employeeApi.fetchAvailableSupervisors().catch(error => {
          console.warn('Failed to fetch supervisors:', error);
          return [];
        })
      ]);
      
      // Combine all users into a single array
      const allUsers = [...guardsData, ...supervisorsData];
      
      // Create a map for quick lookup by user_id
      const userDetailsMap = {};
      allUsers.forEach(user => {
        const userId = user.user_id || user.employee_id;
        if (userId) {
          userDetailsMap[userId] = {
            userId,
            name: user.user_name || user.name || user.employee_name || 'Unknown',
            user_name: user.user_name || user.name || user.employee_name || 'Unknown',
            role_name: user.spec_role || user.role_name || 'Unknown',
            ...user
          };
        }
      });
      
      // For any user IDs not found in the bulk data, create Unknown entries
      userIds.forEach(userId => {
        if (!userDetailsMap[userId]) {
          userDetailsMap[userId] = {
            userId,
            name: 'Unknown',
            user_name: 'Unknown',
            role_name: 'Unknown'
          };
        }
      });
      
      // Cache profile images for all users that have image_path
      // This will pre-populate the cache so ProfileImage components can use cached URLs immediately
      const cacheProfileImages = async () => {
        const usersWithImages = Object.values(userDetailsMap)
          .filter(user => (user.image_path || user.image_presigned_url) && 
                         (user.image_path?.trim() !== '' || user.image_presigned_url?.trim() !== ''));
        
        const cachePromises = usersWithImages.map(user => cacheUserProfileImage(user));
        
        // Wait for all cache operations to complete (but don't block on presigned URL fetching)
        await Promise.allSettled(cachePromises);
        console.log(`📦 Profile image caching initiated for ${cachePromises.length} users`);
      };
      
      // Start caching profile images in the background (don't await to avoid blocking)
      cacheProfileImages().catch(error => {
        console.warn('⚠️ Error during profile image caching:', error);
      });
      
      console.log('🔍 Fetched user details map from bulk API:', userDetailsMap);
      return userDetailsMap;
    } catch (error) {
      console.error('Error fetching user details:', error);
      return {};
    }
  };
  
  // Fetch all sites when component mounts
  useEffect(() => {
    fetchAllSites();
  }, []);

  // Read URL parameters and populate form when component mounts or URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const siteId = params.get('siteId');
    const siteName = params.get('siteName');
    const date = params.get('date');
    const shiftIndex = params.get('shiftIndex');
    const shiftStartTime = params.get('shiftStartTime');
    const shiftEndTime = params.get('shiftEndTime');
    
    // If URL parameters are present, populate the form
    if (siteId && allSites.length > 0) {
      // Find the site by ID
      const site = allSites.find(s => s.site_id === siteId || s.id === siteId);
      if (site) {
        setSelectedSite(site);
        setSearchTerm(siteName || site.site_name || '');
      }
    }
    
    // Set the date if provided in URL parameters
    if (date) {
      const dateObj = new Date(date);
      setDatePickerData({
        mode: 'single',
        startDate: null,
        endDate: null,
        singleDate: dateObj
      });
      setSelectedDate(date);
      console.log('Set date from URL parameter:', date);
    }
    
    // If shift information is provided, we need to wait for shifts to load
    // and then select the appropriate shift
    if (shiftIndex || shiftStartTime || shiftEndTime) {
      console.log('URL parameters provide shift info:', { shiftIndex, shiftStartTime, shiftEndTime });
      // Note: We'll handle shift selection after shifts are loaded
    }
  }, [location.search, allSites, setDatePickerData]);

  // Fetch shifts when site and date are selected
  useEffect(() => {
    if (selectedSite && datePickerData.mode === 'single' && datePickerData.singleDate) {
      fetchShifts();
    } else if (selectedSite && datePickerData.mode === 'range' && datePickerData.startDate) {
      fetchShifts();
    }
  }, [selectedSite, datePickerData]);

  // Handle shift selection from URL parameters after shifts are loaded
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shiftIndex = params.get('shiftIndex');
    const shiftId = params.get('shiftId');
    
    // If URL parameters specify a shift, select it after shifts are loaded
    if (shiftIndex && shifts.length > 0) {
      const shiftIndexNum = parseInt(shiftIndex);
      // Find shift by index (shift indexes are typically 1-based in the backend)
      const matchingShiftIndex = shifts.findIndex(shift => shift.shift_index === shiftIndexNum.toString());
      
      if (matchingShiftIndex !== -1) {
        console.log(`Selecting shift with index ${shiftIndex} at position ${matchingShiftIndex}`);
        setSelectedShiftIndex(matchingShiftIndex);
      }
    } else if (shiftId && shifts.length > 0) {
      // Find shift by ss_id if provided
      const matchingShiftIndex = shifts.findIndex(shift => shift.ss_id === shiftId);
      
      if (matchingShiftIndex !== -1) {
        console.log(`Selecting shift with ss_id ${shiftId} at position ${matchingShiftIndex}`);
        setSelectedShiftIndex(matchingShiftIndex);
      }
    }
  }, [shifts, location.search]);

  // Function to fetch all sites from API
  const fetchAllSites = async () => {
    try {
      setIsLoading(true);
      const sitesData = await getAllSites();
      console.log('Fetched sites:', sitesData);
      
      // Sort sites alphabetically by name
      const sitesList = Array.isArray(sitesData) ? sitesData : (sitesData?.sites || sitesData?.data || []);
      const sortedSites = [...sitesList].sort((a, b) => {
        const nameA = (a.site_name || a.name || 'Unknown Site').toLowerCase();
        const nameB = (b.site_name || b.name || 'Unknown Site').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setAllSites(sortedSites);
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      // Fallback to empty array if API fails
      setAllSites([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch shifts from API - Updated to match Flutter structure
  const fetchShifts = async () => {
    if (!selectedSite) return;
    
    try {
      setIsLoadingShifts(true);
      setShiftsError(null);
      
      // Determine the date to use
      let targetDate;
      if (datePickerData.mode === 'single') {
        targetDate = datePickerData.singleDate;
      } else if (datePickerData.mode === 'range') {
        targetDate = datePickerData.startDate;
      } else {
        return;
      }
      
      // Format date as YYYY-MM-DD
      const formattedDate = targetDate.toISOString().split('T')[0];
      
      const shiftsData = await getShiftRoster(selectedSite.site_id, formattedDate);
      
      // Log API event for viewing shifts
      loggingService.logInfo(
        'shift_view',
        `User viewed shifts for site ${selectedSite.site_id} on date ${formattedDate}`,
        {
          endpoint: '/shift/roster',
          method: 'GET',
          siteId: selectedSite.site_id,
          date: formattedDate,
          shiftCount: Array.isArray(shiftsData) ? shiftsData.length : 0,
        }
      ).catch(logError => {
        console.warn('[ScheduleShift] Failed to log event:', logError);
      });
      
      // Collect all unique user IDs from all shifts
      const allUserIds = new Set();
      shiftsData.forEach(shift => {
        if (shift.user_datas && Array.isArray(shift.user_datas)) {
          shift.user_datas.forEach(userData => {
            if (userData.user_id) {
              allUserIds.add(userData.user_id);
            }
          });
        }
      });

      // Fetch user details for all unique user IDs
      const userDetailsMap = await fetchUserDetails(Array.from(allUserIds));
      console.log('🔍 Fetched user details map:', userDetailsMap);
      
      // Transform API response to our shift format
      const transformedShifts = shiftsData.map((shift, index) => {
        // Separate user_datas into guards and supervisors based on role_name
        const guards = [];
        const supervisors = [];
        const zoneGuards = {};
        
        if (shift.user_datas && Array.isArray(shift.user_datas)) {
          shift.user_datas.forEach(userData => {
            const userDetails = userDetailsMap[userData.user_id] || {};
            const userInfo = {
              user_id: userData.user_id,
              name: userDetails.name || userData.user_name || userData.name || 'Unknown',
              user_name: userDetails.user_name || userData.user_name || userData.name || 'Unknown',
              display_name: userDetails.name || userData.user_name || userData.name || 'Unknown', // Add display_name for zone assignments
              role_name: userData.role_name,
              b_id: userData.b_id || '',
              notes: userData.user_notes || userData.notes || '',
              image_path: userDetails.image_path || userData.image_path || '', // Include image_path for caching
              ...userDetails // Include all user details
            };
            
            // Cache profile image if available
            if (userInfo.image_path || userDetails.image_path) {
              cacheUserProfileImage(userInfo).catch(error => {
                console.warn(`⚠️ Failed to cache profile image for user ${userInfo.user_id}:`, error);
              });
            }
            
            if (userData.role_name === 'guard') {
              guards.push(userInfo);
              
              // If guard has a zone assignment (b_id), add to zoneGuards
              if (userData.b_id) {
                if (!zoneGuards[userData.b_id]) {
                  zoneGuards[userData.b_id] = [];
                }
                zoneGuards[userData.b_id].push(userInfo);
              }
            } else if (userData.role_name === 'supervisor') {
              supervisors.push(userInfo);
            }
          });
        }
        
        return {
          ss_id: shift.ss_id,
          site_id: shift.site_id,
          date: shift.date,
          shift_start_time: shift.shift_start_time,
          shift_end_time: shift.shift_end_time,
          shift_type: shift.shift_type,
          status: shift.status,
          notes: shift.notes,
          is_overtime: shift.is_overtime,
          break_duration: shift.break_duration,
          shift_index: index + 1,
          isNew: false,
          from_timestamp: shift.shift_start_timestamp || '',
          to_timestamp: shift.shift_end_timestamp || '',
          // Populate employee assignments from API data
          guards: guards,
          supervisors: supervisors,
          guardNotes: shift.guard_notes || [],
          supervisorNotes: shift.supervisor_notes || [],
          zoneGuards: zoneGuards
        };
      });
      
      setShifts(transformedShifts);
      setSelectedShiftIndex(0); // Reset to first shift
      
      // If we have shifts, populate the overlay context with the first shift's employee data
      if (transformedShifts.length > 0) {
        const firstShift = transformedShifts[0];
        
        // Set supervisors in context
        if (firstShift.supervisors && firstShift.supervisors.length > 0) {
          setAssignedEmployees(firstShift.supervisors);
        }
        
        // Set guards in context (grouped by zone)
        if (firstShift.zoneGuards && Object.keys(firstShift.zoneGuards).length > 0) {
          setAssignedEmployeesToZones(firstShift.zoneGuards);
        }
        
        console.log('🔍 Populated context with fetched data:', {
          supervisors: firstShift.supervisors,
          zoneGuards: firstShift.zoneGuards
        });
      }
      
    } catch (error) {
      console.error('Failed to fetch shifts:', error);
      setShiftsError(error.message);
      setShifts([]);
      
      // Clear assigned employees and guards when no roster details are found
      setAssignedEmployees([]);
      setAssignedEmployeesToZones({});
    } finally {
      setIsLoadingShifts(false);
    }
  };

  
  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date for display
  const formatDateDisplay = () => {
    if (datePickerData.mode === 'single') {
      return formatDate(datePickerData.singleDate);
    } else {
      return `${formatDate(datePickerData.startDate)} to ${formatDate(datePickerData.endDate)}`;
    }
  };

  // Format time for display
  const formatTimeDisplay = (time) => {
    const hours = time.hours.toString().padStart(2, '0');
    const minutes = time.minutes.toString().padStart(2, '0');
    return `${hours}:${minutes} ${time.period}`;
  };

  // Calculate current shift duration
  const getCurrentShiftDuration = () => {
    if (shifts.length === 0 || selectedShiftIndex >= shifts.length) {
      return 0;
    }
    
    const currentShift = shifts[selectedShiftIndex];
    if (!currentShift.shift_start_time || !currentShift.shift_end_time) {
      return 0;
    }
    
    const startTime = parseTimeString(currentShift.shift_start_time);
    const endTime = parseTimeString(currentShift.shift_end_time);
    
    if (!startTime || !endTime) {
      return 0;
    }
    
    return getShiftDuration(startTime, endTime);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Submitting shift:', { 
      shiftType, 
      selectedDate, 
      selectedOfficers, 
      dateMode: datePickerData.mode,
      singleDate: datePickerData.mode === 'single' ? datePickerData.singleDate : null,
      dateRange: datePickerData.mode === 'range' ? { startDate: datePickerData.startDate, endDate: datePickerData.endDate } : null
    });
    // Add your API call logic here
  };

  // Handle search functionality
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    if (value.length > 0 && allSites.length > 0) {
      // Filter through real site data
      const filteredSites = allSites.filter(site => 
        site.site_name && site.site_name.toLowerCase().includes(value.toLowerCase())
      );
      setSearchSuggestions(filteredSites.map(site => site.site_name));
    } else if (value.length === 0 && allSites.length > 0) {
      // Show all sites when search is empty
      setSearchSuggestions(allSites.map(site => site.site_name));
    } else {
      setSearchSuggestions([]);
    }
  };

  // Handle search bar focus - show all sites
  const handleSearchFocus = () => {
    if (allSites.length > 0) {
      setSearchSuggestions(allSites.map(site => site.site_name));
      setShowAllSitesDropdown(true);
    }
  };

  // Handle search bar blur
  const handleSearchBlur = () => {
    // Delay hiding to allow clicking on suggestions
    setTimeout(() => {
      setShowAllSitesDropdown(false);
    }, 200);
  };

  // Show all sites when search bar is clicked/focused
  const handleSearchBarClick = () => {
    if (allSites.length > 0) {
      setSearchSuggestions(allSites.map(site => site.site_name));
      setShowAllSitesDropdown(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.querySelector('.search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowAllSitesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearchSubmit = (value) => {
    console.log('Search submitted:', value);
    // Find the selected site from allSites
    const site = allSites.find(s => s.site_name === value);
    console.log('Site selection:', { value, site, allSitesCount: allSites.length });
    if (site) {
      setSelectedSite(site);
      console.log('Selected site set:', site);
    } else {
      console.log('Site not found for value:', value);
    }
    setShowAllSitesDropdown(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion);
    setSearchSuggestions([]);
    setShowAllSitesDropdown(false);
    // Find and set the selected site with full information
    const site = allSites.find(s => s.site_name === suggestion);
    console.log('Suggestion click:', { suggestion, site, allSitesCount: allSites.length });
    if (site) {
      setSelectedSite(site);
      console.log('Selected site set from suggestion:', site);
    } else {
      console.log('Site not found for suggestion:', suggestion);
    }
  };

  const clearSelectedSite = () => {
    setSelectedSite(null);
    setSearchTerm('');
    setSearchSuggestions([]);
    setShowAllSitesDropdown(false);
    // Clear shifts when site is cleared
    setShifts([]);
    setSelectedShiftIndex(0);
    setShiftsError(null);
  };

  // Persist the employees/guards currently selected in context onto the specified shift
  const persistCurrentAssignmentsToShift = (shiftIndex) => {
    if (shiftIndex == null || shiftIndex < 0 || shiftIndex >= shifts.length) return;

    const cloneEmployees = (employees = []) =>
      employees.map(emp => (typeof emp === 'string' ? emp : { ...emp }));

    const cloneZoneAssignments = (zoneAssignments = {}) => {
      const cloned = {};
      Object.entries(zoneAssignments).forEach(([zoneId, guards]) => {
        cloned[zoneId] = (guards || []).map(guard => (typeof guard === 'string' ? guard : { ...guard }));
      });
      return cloned;
    };

    setShifts(prevShifts =>
      prevShifts.map((shift, index) => {
        if (index !== shiftIndex) return shift;

        const supervisors = cloneEmployees(assignedEmployees || []);
        const zoneGuards = cloneZoneAssignments(assignedEmployeesToZones || {});
        const guards = Object.values(zoneGuards).flat();

        return {
          ...shift,
          supervisors,
          zoneGuards,
          guards
        };
      })
    );
  };

  // Shift management functions - Updated to match Flutter structure
  const handleAddShift = () => {
    // Persist the current shift assignments before resetting for a new shift tab
    persistCurrentAssignmentsToShift(selectedShiftIndex);

    // Validate that the shift is not for a previous date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    let targetDate;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else {
      targetDate = new Date();
    }
    
    if (targetDate) {
      const shiftDate = new Date(targetDate);
      shiftDate.setHours(0, 0, 0, 0); // Reset time to start of day
      if (shiftDate < today) {
        alert('Cannot add shifts for previous dates. Please select today or a future date.');
        return;
      }
    }

    // If no shifts exist, create the first one
    if (shifts.length === 0) {
      // console.log("🔍 No shifts exist, creating first shift");
      const defaultShift = createDefaultShift();
      setShifts([defaultShift]);
      setSelectedShiftIndex(0);
      return;
    }
    
    const newShiftIndex = shifts.length + 1;
    
    // Use default time 9:00 AM to 10:00 AM (1 hour shift) for new shifts
    const defaultStartTime = { hours: 9, minutes: 0, period: 'AM' };
    const defaultEndTime = { hours: 10, minutes: 0, period: 'AM' };
    
    // Generate timestamps with default time
    const timestamps = generateShiftTimestamps(defaultStartTime, defaultEndTime, targetDate);
    
    const newShift = {
      ss_id: `new_shift_${Date.now()}`, // Temporary ID for new shifts
      site_id: selectedSite?.site_id || '',
      date: targetDate.toISOString().split('T')[0],
      shift_start_time: formatTimeDisplay(defaultStartTime),
      shift_end_time: formatTimeDisplay(defaultEndTime),
      shift_type: timestamps.is_overnight ? 'night' : 'day',
      status: 'draft',
      notes: '',
      is_overtime: timestamps.is_overnight,
      break_duration: 60,
      shift_index: newShiftIndex,
      isNew: true,
      from_timestamp: timestamps.from_timestamp,
      to_timestamp: timestamps.to_timestamp,
      // Add empty arrays for employee assignments
      guards: [],
      supervisors: [],
      guardNotes: [],
      supervisorNotes: [],
      zoneGuards: {}
    };
    
    // Clear assigned employees and guards to zones for new shift
    setAssignedEmployees([]);
    setAssignedEmployeesToZones({});
    
    // Update context with default time values
    setStartTimeData(defaultStartTime);
    setEndTimeData(defaultEndTime);
    
    setShifts(prevShifts => [...prevShifts, newShift]);
    setSelectedShiftIndex(shifts.length); // Select the newly added shift
  };

  const handleRemoveShift = async (shiftIndex) => {
    // 1. Minimum Shift Validation
    if (shifts.length <= 1) {
      console.log("❌ Cannot delete - only one shift remaining");
      alert('At least one shift must remain. Cannot delete the last shift.');
      return;
    }

    // 2. Past Date Validation
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    let shiftDate = null;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      shiftDate = new Date(datePickerData.singleDate);
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      shiftDate = new Date(datePickerData.startDate);
    }
    
    if (shiftDate) {
      shiftDate.setHours(0, 0, 0, 0); // Reset time to start of day
      if (shiftDate < today) {
        console.log("❌ Cannot delete - shift is for a past date");
        alert('Cannot modify shifts for past dates.');
        return;
      }
    }

    // 3. Show warning modal using overlay system with actual deletion function
    openOverlay('deleteShiftWarning', { 
      shiftIndex,
      onConfirm: async (indexToDelete) => {
        try {
          // Get the shift's ss_id if it exists
          const shift = shifts[indexToDelete];
          const ssId = shift?.ss_id;

          if (ssId && !ssId.startsWith('default_shift_') && !ssId.startsWith('new_shift_')) {
            console.log("📡 Calling API to delete shift roster...", ssId);
            // Call the API to delete the shift
            await deleteShiftRoster(ssId);
            console.log("✅ API call successful - shift deleted from backend");
            alert('Shift deleted successfully!');
          } else {
            console.log("ℹ️ No ss_id found - this was a new shift not yet saved to backend");
          }

          // 5. UI State Updates After Successful Deletion
          setShifts(prevShifts => {
            const updatedShifts = prevShifts.filter((_, index) => index !== indexToDelete);
            
            // Adjust selected shift index if needed
            let newSelectedIndex = selectedShiftIndex;
            if (selectedShiftIndex >= updatedShifts.length) {
              newSelectedIndex = Math.max(0, updatedShifts.length - 1);
            }
            setSelectedShiftIndex(newSelectedIndex);
            
            return updatedShifts;
          });

          // Clear zone-guard assignments for the remaining shifts
          setAssignedEmployeesToZones({});
          
          // Update context with the new selected shift's time if there are remaining shifts
          if (shifts.length > 1) {
            const newSelectedShift = shifts[selectedShiftIndex >= shifts.length - 1 ? shifts.length - 2 : selectedShiftIndex];
            if (newSelectedShift?.shift_start_time && newSelectedShift?.shift_end_time) {
              const parseTimeString = (timeString) => {
                const [time, period] = timeString.split(' ');
                const [hours, minutes] = time.split(':');
                return {
                  hours: parseInt(hours),
                  minutes: parseInt(minutes),
                  period: period
                };
              };
              
              const startTime = parseTimeString(newSelectedShift.shift_start_time);
              const endTime = parseTimeString(newSelectedShift.shift_end_time);
              
              setStartTimeData(startTime);
              setEndTimeData(endTime);
            }
          }

          console.log("✅ Shift deletion process completed successfully");
          
        } catch (error) {
          console.error("❌ Error during deletion process:", error);
          alert(`Error deleting shift: ${error.message}`);
        }
      }
    });
  };

  const handleSelectShift = (shiftIndex) => {
    if (shiftIndex === selectedShiftIndex) return;

    // Save the current shift assignments before switching tabs
    persistCurrentAssignmentsToShift(selectedShiftIndex);

    setIsSwitchingShift(true); // Set flag to indicate we're switching shifts
    setSelectedShiftIndex(shiftIndex);
    
    // Update context with the selected shift's employee data
    if (shifts[shiftIndex]) {
      const selectedShift = shifts[shiftIndex];
      
      // Set supervisors in context
      if (selectedShift.supervisors && selectedShift.supervisors.length > 0) {
        setAssignedEmployees(selectedShift.supervisors);
      } else {
        setAssignedEmployees([]);
      }
      
      // Set guards in context (grouped by zone)
      if (selectedShift.zoneGuards && Object.keys(selectedShift.zoneGuards).length > 0) {
        setAssignedEmployeesToZones(selectedShift.zoneGuards);
      } else {
        setAssignedEmployeesToZones({});
      }
      
      // Update context time data with the selected shift's time
      if (selectedShift.shift_start_time && selectedShift.shift_end_time) {
        // Parse the shift times back to time objects
        const parseTimeString = (timeString) => {
          const [time, period] = timeString.split(' ');
          const [hours, minutes] = time.split(':');
          return {
            hours: parseInt(hours),
            minutes: parseInt(minutes),
            period: period
          };
        };
        
        const startTime = parseTimeString(selectedShift.shift_start_time);
        const endTime = parseTimeString(selectedShift.shift_end_time);
        
        setStartTimeData(startTime);
        setEndTimeData(endTime);
        
        console.log('🔍 Updated context time for shift', shiftIndex, ':', {
          startTime,
          endTime
        });
      }
      
      console.log('🔍 Updated context for shift', shiftIndex, ':', {
        supervisors: selectedShift.supervisors,
        zoneGuards: selectedShift.zoneGuards
      });
    } else {
      // If no shift exists at this index, clear the context
      setAssignedEmployees([]);
      setAssignedEmployeesToZones({});
    }
    
    // Reset the flag after a short delay to allow the time update to complete
    setTimeout(() => {
      setIsSwitchingShift(false);
    }, 100);
  };

  // Update shift timestamps when time changes
  const updateShiftTimestamps = (shiftIndex, startTime, endTime) => {
    if (shiftIndex < 0 || shiftIndex >= shifts.length) return;
    
    // Determine target date
    let targetDate;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else {
      targetDate = new Date();
    }
    
    // Generate new timestamps
    const timestamps = generateShiftTimestamps(startTime, endTime, targetDate);
    
    // Update the shift
    setShifts(prevShifts => prevShifts.map((shift, index) => {
      if (index === shiftIndex) {
        return {
          ...shift,
          shift_start_time: formatTimeDisplay(startTime),
          shift_end_time: formatTimeDisplay(endTime),
          shift_type: timestamps.is_overnight ? 'night' : 'day',
          is_overtime: timestamps.is_overnight,
          from_timestamp: timestamps.from_timestamp,
          to_timestamp: timestamps.to_timestamp
        };
      }
      return shift;
    }));
  };

  // Create default shift when none exist - Updated to match Flutter structure
  const createDefaultShift = () => {
    // Use default time 9:00 AM to 10:00 AM (1 hour shift) for new shifts
    const defaultStartTime = { hours: 9, minutes: 0, period: 'AM' };
    const defaultEndTime = { hours: 10, minutes: 0, period: 'AM' };
    
    // Determine target date
    let targetDate;
    if (datePickerData.mode === 'single' && datePickerData.singleDate) {
      targetDate = datePickerData.singleDate;
    } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
      targetDate = datePickerData.startDate;
    } else {
      targetDate = new Date();
    }
    
    // Validate that the shift is not for a previous date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    if (targetDate) {
      const shiftDate = new Date(targetDate);
      shiftDate.setHours(0, 0, 0, 0); // Reset time to start of day
      if (shiftDate < today) {
        // If the selected date is in the past, use today's date instead
        targetDate = new Date();
        console.warn('Selected date is in the past, using today\'s date instead');
      }
    }
    
    // Generate timestamps with default time
    const timestamps = generateShiftTimestamps(defaultStartTime, defaultEndTime, targetDate);
    
    const defaultShift = {
      ss_id: `default_shift_${Date.now()}`,
      site_id: selectedSite?.site_id || '',
      date: targetDate.toISOString().split('T')[0],
      shift_start_time: formatTimeDisplay(defaultStartTime),
      shift_end_time: formatTimeDisplay(defaultEndTime),
      shift_type: timestamps.is_overnight ? 'night' : 'day',
      status: 'draft',
      notes: '',
      is_overtime: timestamps.is_overnight,
      break_duration: 60,
      shift_index: 1,
      isNew: true,
      from_timestamp: timestamps.from_timestamp,
      to_timestamp: timestamps.to_timestamp,
      // Add empty arrays for employee assignments
      guards: [],
      supervisors: [],
      guardNotes: [],
      supervisorNotes: [],
      zoneGuards: {}
    };
    
    return defaultShift;
  };

  // Effect to create default shift when none exist
  useEffect(() => {
    // console.log("🔍 Default shift effect triggered:", {
    //   shiftsLength: shifts.length,
    //   selectedSite: !!selectedSite,
    //   selectedSiteName: selectedSite?.site_name,
    //   datePickerData: datePickerData,
    //   hasValidDate: !!(datePickerData.startDate || datePickerData.singleDate),
    //   shiftsError: shiftsError,
    //   isLoadingShifts: isLoadingShifts
    // });
    
    // Only create default shift if:
    // 1. No shifts exist
    // 2. Site is selected
    // 3. Date is selected
    // 4. Not currently loading shifts
    // 5. Date is not in the past
    // Note: We create default shift even if there's an API error (shiftsError)
    if (shifts.length === 0 && 
        selectedSite && 
        (datePickerData.startDate || datePickerData.singleDate) && 
        !isLoadingShifts) {
      
      // Check if the selected date is not in the past
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      let selectedDate = null;
      if (datePickerData.mode === 'single' && datePickerData.singleDate) {
        selectedDate = new Date(datePickerData.singleDate);
      } else if (datePickerData.mode === 'range' && datePickerData.startDate) {
        selectedDate = new Date(datePickerData.startDate);
      }
      
      if (selectedDate) {
        selectedDate.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          console.log("🔍 Selected date is in the past, not creating default shift");
          return;
        }
      }
      
      console.log("🔍 Creating default shift - Shift 1");
      const defaultShift = createDefaultShift();
      console.log("🔍 Default shift created:", defaultShift);
      // Calculate and log the shift duration
      const startTime = parseTimeString(defaultShift.shift_start_time);
      const endTime = parseTimeString(defaultShift.shift_end_time);
      const duration = getShiftDuration(startTime, endTime);
      
      console.log("🔍 Shift duration calculation:", {
        startTime: defaultShift.shift_start_time,
        endTime: defaultShift.shift_end_time,
        parsedStartTime: startTime,
        parsedEndTime: endTime,
        calculatedDuration: duration,
        from_timestamp: defaultShift.from_timestamp,
        to_timestamp: defaultShift.to_timestamp
      });
      setShifts([defaultShift]);
      setSelectedShiftIndex(0);
      
      // Clear assigned employees and guards to zones for new default shift
      setAssignedEmployees([]);
      setAssignedEmployeesToZones({});
      
      // Update context with default time values
      setStartTimeData({ hours: 9, minutes: 0, period: 'AM' });
      setEndTimeData({ hours: 10, minutes: 0, period: 'AM' });
    }
  }, [selectedSite, datePickerData, isLoadingShifts]);

  // Effect to update timestamps when time data changes (only when user changes time, not when switching shifts)
  useEffect(() => {
    if (shifts.length > 0 && selectedShiftIndex >= 0 && selectedShiftIndex < shifts.length && !isSwitchingShift) {
      const currentShift = shifts[selectedShiftIndex];
      
      // Check if this is a new shift (not yet saved)
      const isNewShift = currentShift.isNew || 
                        (currentShift.ss_id && (
                          currentShift.ss_id.startsWith('default_shift_') || 
                          currentShift.ss_id.startsWith('new_shift_')
                        ));
      
      // If it's a new shift and has employees assigned, clear them when time changes
      if (isNewShift) {
        const hasSupervisors = assignedEmployees && assignedEmployees.length > 0;
        const hasGuards = assignedEmployeesToZones && Object.keys(assignedEmployeesToZones).length > 0 && 
                         Object.values(assignedEmployeesToZones).some(guards => guards && guards.length > 0);
        
        if (hasSupervisors || hasGuards) {
          console.log('🔍 Clearing employees for new shift due to time change');
          setAssignedEmployees([]);
          setAssignedEmployeesToZones({});
        }
      }
      
      updateShiftTimestamps(selectedShiftIndex, startTimeData, endTimeData);
    }
  }, [startTimeData, endTimeData, selectedShiftIndex, isSwitchingShift]);

  // Check if current shift is complete and saved (same logic as Flutter)
  const isCurrentShiftComplete = () => {
    // If no shifts exist, allow creating new shifts
    if (shifts.length === 0) {
      console.log("🔍 No shifts exist - allowing shift creation");
      return true; // Allow creating shifts when none exist
    }

    // If selectedShiftIndex is invalid, disable Add Shift
    if (selectedShiftIndex >= shifts.length) {
      console.log("🔍 Invalid shift index - disabling Add Shift button");
      return false;
    }

    const currentShift = shifts[selectedShiftIndex];

    // Check if current shift has required fields filled
    const hasTimeSet = (currentShift.shift_start_time && currentShift.shift_start_time.trim() !== '') &&
                      (currentShift.shift_end_time && currentShift.shift_end_time.trim() !== '');
    
    const hasSupervisors = assignedEmployees && assignedEmployees.length > 0;
    const hasGuards = assignedEmployeesToZones && Object.keys(assignedEmployeesToZones).length > 0 && 
                     Object.values(assignedEmployeesToZones).some(guards => guards && guards.length > 0);
    
    const hasBeenSaved = currentShift.ss_id && 
                        currentShift.ss_id.toString().trim() !== '' && 
                        !currentShift.ss_id.startsWith('default_shift_') &&
                        !currentShift.ss_id.startsWith('new_shift_');
    const isNotNew = !currentShift.isNew; // Check if shift is not marked as new

    // A shift is complete if it has time set, has staff assigned, and either has ss_id or is not new
    const isComplete = hasTimeSet &&
                      (hasSupervisors || hasGuards) &&
                      (hasBeenSaved || isNotNew);

    console.log("🔍 Shift completion check for shift", currentShift.shift_index || 'unknown', ":");
    console.log("🔍 Has time set:", hasTimeSet, `(${currentShift.shift_start_time} - ${currentShift.shift_end_time})`);
    console.log("🔍 Has supervisors:", hasSupervisors, `(${assignedEmployees?.length || 0})`);
    console.log("🔍 Has guards:", hasGuards, `(${assignedEmployeesToZones ? Object.keys(assignedEmployeesToZones).length : 0})`);
    console.log("🔍 Has been saved:", hasBeenSaved, `(ss_id: '${currentShift.ss_id}')`);
    console.log("🔍 Is not new:", isNotNew, `(isNew: ${currentShift.isNew})`);
    console.log("🔍 Is complete:", isComplete);

    return isComplete;
  };

  const handleOpenAssignEmployees = () => {
    // Ensure at least one shift exists before assigning employees
    if (shifts.length === 0 && selectedSite) {
      // console.log("🔍 No shifts exist, creating first shift before assigning employees");
      const defaultShift = createDefaultShift();
      setShifts([defaultShift]);
      setSelectedShiftIndex(0);
    }
    
    // Prepare shift validation data for employee availability checking
    const currentShift = shifts[selectedShiftIndex];
    const targetDate = datePickerData.mode === 'single' 
      ? datePickerData.singleDate 
      : datePickerData.startDate;
    
    const shiftValidationData = {
      selectedSite: selectedSite,
      currentShift: currentShift,
      date: targetDate ? targetDate.toISOString().split('T')[0] : null,
      startTime: currentShift?.shift_start_time || formatTimeDisplay(startTimeData),
      endTime: currentShift?.shift_end_time || formatTimeDisplay(endTimeData)
    };
    
    openOverlay('assignEmployees', { 
      employeeType: 'supervisors',
      shiftValidationData: shiftValidationData
    });
  };

  const handleRemoveEmployee = (index) => {
    const updatedEmployees = assignedEmployees.filter((_, i) => i !== index);
    setAssignedEmployees(updatedEmployees);
  };

  const handleDeleteEmployee = (index) => {
    console.log('Delete employee:', assignedEmployees[index]);
    // Add your delete employee logic here
  };

  const handleEmployeeNoteChange = (updatedEmployee, newNote) => {
    // Update the assignedEmployees array with the new note
    setAssignedEmployees(prevEmployees => 
      prevEmployees.map(emp => {
        const employeeKey = typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id);
        const updatedEmployeeKey = typeof updatedEmployee === 'string' ? updatedEmployee : (updatedEmployee.user_id || updatedEmployee.employee_id);
        
        if (employeeKey === updatedEmployeeKey) {
          return {
            ...emp,
            notes: newNote,
            user_notes: newNote
          };
        }
        return emp;
      })
    );
  };

  // Zone assignment handlers - now handled by ZoneAssignmentManager component

  // Schedule shift handler - Updated to match Flutter implementation
  const handleScheduleShift = async () => {
    try {
      setIsScheduling(true);
      setScheduleError(null);
      setScheduleSuccess(false);

      // Comprehensive validation
      if (!selectedSite) {
        throw new Error('Please select a site first');
      }

      if (shifts.length === 0) {
        throw new Error('No shifts to schedule');
      }

      const currentShift = shifts[selectedShiftIndex];
      if (!currentShift) {
        throw new Error('No shift selected');
      }

      // Validate shift times
      if (!currentShift.shift_start_time || !currentShift.shift_end_time) {
        throw new Error('Please set both start and end times for the shift');
      }

      // Validate that at least one employee is assigned
      const hasSupervisors = assignedEmployees && assignedEmployees.length > 0;
      const hasGuards = assignedEmployeesToZones && Object.keys(assignedEmployeesToZones).length > 0 && 
                       Object.values(assignedEmployeesToZones).some(guards => guards && guards.length > 0);
      
      if (!hasSupervisors && !hasGuards) {
        throw new Error('Please assign at least one employee (supervisor or guard) to the shift');
      }

      // Validate that the shift is not for a previous date
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      if (datePickerData.mode === 'single') {
        if (datePickerData.singleDate) {
          const shiftDate = new Date(datePickerData.singleDate);
          shiftDate.setHours(0, 0, 0, 0);
          if (shiftDate < today) {
            throw new Error('Cannot schedule shifts for previous dates. Please select today or a future date.');
          }
        }
      } else if (datePickerData.mode === 'range') {
        if (datePickerData.startDate && datePickerData.endDate) {
          const startDate = new Date(datePickerData.startDate);
          const endDate = new Date(datePickerData.endDate);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          
          // Check if start date is in the past
          if (startDate < today) {
            throw new Error('Cannot schedule shifts for previous dates. Please select today or a future date for the start date.');
          }
          
          // Check if end date is in the past
          if (endDate < today) {
            throw new Error('Cannot schedule shifts for previous dates. Please select today or a future date for the end date.');
          }
          
          // Check if end date is before start date
          if (endDate < startDate) {
            throw new Error('End date cannot be before start date. Please select a valid date range.');
          }
        }
      }

      // Debug: Log assigned employees data
      console.log('🔍 Debug - Assigned Employees:', assignedEmployees);
      console.log('🔍 Debug - Assigned Employees to Zones:', assignedEmployeesToZones);
      console.log('🔍 Debug - Current Shift:', currentShift);
      console.log('🔍 Debug - Selected Site:', selectedSite);
      console.log('🔍 Debug - Date Picker Data:', datePickerData);
      
      // Check if this is an update operation
      const isUpdateOperation = currentShift.ss_id && 
        currentShift.ss_id.toString().trim() !== '' && 
        !currentShift.ss_id.startsWith('default_shift_') && 
        !currentShift.ss_id.startsWith('new_shift_');
      
      console.log('🔍 Debug - Is Update Operation:', isUpdateOperation, 'SS_ID:', currentShift.ss_id);

      // Prepare shift data following Flutter structure
      const shiftData = {
        site_id: selectedSite.site_id,
        date: datePickerData.mode === 'single' 
          ? datePickerData.singleDate?.toISOString().split('T')[0]
          : datePickerData.startDate?.toISOString().split('T')[0],
        date_range: datePickerData.mode === 'range' ? [
          datePickerData.startDate?.toISOString().split('T')[0],
          datePickerData.endDate?.toISOString().split('T')[0]
        ] : null,
        // Add mode information for API
        mode: datePickerData.mode,
        shifts: [{
          index: selectedShiftIndex + 1, // Visual shift index
          shift_start_time: currentShift.shift_start_time,
          shift_end_time: currentShift.shift_end_time,
          shift_start_timestamp: currentShift.from_timestamp,
          shift_end_timestamp: currentShift.to_timestamp,
          user_ids: [
            // Collect all user IDs (supervisors + guards)
            ...(assignedEmployees || []).map(emp => 
              typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id)
            ),
            ...Object.values(assignedEmployeesToZones || {}).flat().map(guard => 
              typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id)
            )
          ],
          user_datas: [
            // Add assigned employees (supervisors)
            ...(assignedEmployees || []).map(emp => {
              console.log('🔍 Processing supervisor employee:', emp);
              return {
                user_id: typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id),
                user_name: typeof emp === 'string' ? emp : (emp.user_name || emp.name || emp.display_name || 'Unknown'),
                role_name: 'supervisor',
                b_id: '', // Supervisors don't have zone assignments
                user_notes: emp.notes || emp.user_notes || '',
              };
            }),
            // Add assigned employees to zones (guards)
            ...Object.entries(assignedEmployeesToZones || {}).flatMap(([zoneId, guards]) => {
              console.log('🔍 Processing zone guards for zone:', zoneId, guards);
              return (guards || []).map(guard => {
                console.log('🔍 Processing guard employee:', guard);
                // Get the note from guard object (should be updated via onNoteChange callback)
                const guardNote = guard.notes || guard.user_notes || '';
                console.log('🔍 Guard note:', guardNote);
                return {
                  user_id: typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id),
                  user_name: typeof guard === 'string' ? guard : (guard.user_name || guard.name || guard.display_name || 'Unknown'),
                  role_name: 'guard',
                  b_id: zoneId,
                  user_notes: guardNote,
                };
              });
            })
          ],
          supervisor_notes: (assignedEmployees || []).map(emp => ({
            id: typeof emp === 'string' ? emp : (emp.user_id || emp.employee_id),
            employee: typeof emp === 'string' ? emp : (emp.user_name || emp.name || emp.display_name || 'Unknown'),
            note: emp.notes || emp.user_notes || ''
          })),
          guard_notes: Object.entries(assignedEmployeesToZones || {}).flatMap(([zoneId, guards]) => 
            (guards || []).map(guard => {
              const guardNote = guard.notes || guard.user_notes || '';
              console.log('🔍 Guard note for guard_notes array:', guardNote);
              return {
                id: typeof guard === 'string' ? guard : (guard.user_id || guard.employee_id),
                employee: typeof guard === 'string' ? guard : (guard.user_name || guard.name || guard.display_name || 'Unknown'),
                note: guardNote
              };
            })
          ),
          site_id: selectedSite.site_id,
          tenant_id: "0",
          event_timestamp: Date.now().toString(),
          site_details: {
            site_id: selectedSite.site_id,
            site_name: selectedSite.site_name,
            address: selectedSite.address,
            activity_timestamp: Date.now().toString(),
            geofence_details: JSON.stringify(selectedSite.geofence_details || {}),
          },
          ...(currentShift.ss_id && !currentShift.ss_id.startsWith('default_shift_') && { ss_id: currentShift.ss_id }) // Include ss_id if updating existing shift
        }]
      };

      console.log('Sending shift data:', shiftData);
      console.log('Date picker mode:', datePickerData.mode);
      console.log('Date range info:', {
        startDate: datePickerData.startDate?.toISOString().split('T')[0],
        endDate: datePickerData.endDate?.toISOString().split('T')[0],
        isRange: datePickerData.mode === 'range'
      });

      // Send to API
      let response;
      
      if (datePickerData.mode === 'range') {
        // For date ranges, try the API first, then fallback to individual calls
        console.log('Scheduling shift for date range...');
        
        try {
          response = await sendShiftData(selectedSite.site_id, shiftData);
          
          // Check if the API response indicates it processed all dates
          if (response && response.processed_dates) {
            console.log('API processed dates:', response.processed_dates);
          } else {
            console.warn('API response does not indicate processed dates for range, trying individual calls...');
            
            // Fallback: Make individual API calls for each date in the range
            // Skip the first date since it was already processed by the initial API call
            const startDate = new Date(datePickerData.startDate);
            const endDate = new Date(datePickerData.endDate);
            const responses = [];
            
            // Start from the day after the start date to avoid duplicating the first date
            for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
              // Skip the first iteration to avoid duplicating the first date
              if (date.getTime() === startDate.getTime()) {
                continue;
              }
              
              const singleDateData = {
                ...shiftData,
                date: date.toISOString().split('T')[0],
                date_range: null, // Remove date_range for individual calls
                mode: 'single'
              };
              
              console.log(`Scheduling shift for individual date: ${date.toISOString().split('T')[0]}`);
              const individualResponse = await sendShiftData(selectedSite.site_id, singleDateData);
              responses.push(individualResponse);
            }
            
            response = {
              status: 'success',
              message: `Shift scheduled for ${responses.length + 1} dates`, // +1 for the first date processed by initial call
              processed_dates: responses.length + 1,
              individual_responses: responses
            };
          }
        } catch (error) {
          console.error('Error with range API call, trying individual calls:', error);
          
          // Fallback: Make individual API calls for each date in the range
          const startDate = new Date(datePickerData.startDate);
          const endDate = new Date(datePickerData.endDate);
          const responses = [];
          
          for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
            const singleDateData = {
              ...shiftData,
              date: date.toISOString().split('T')[0],
              date_range: null, // Remove date_range for individual calls
              mode: 'single'
            };
            
            console.log(`Scheduling shift for individual date: ${date.toISOString().split('T')[0]}`);
            const individualResponse = await sendShiftData(selectedSite.site_id, singleDateData);
            responses.push(individualResponse);
          }
          
          response = {
            status: 'success',
            message: `Shift scheduled for ${responses.length} dates`,
            processed_dates: responses.length,
            individual_responses: responses
          };
        }
      } else {
        // Single date scheduling
        response = await sendShiftData(selectedSite.site_id, shiftData);
      }
      
      console.log('Shift scheduled successfully:', response);
      console.log('API Response details:', {
        status: response.status || 'unknown',
        message: response.message || 'no message',
        data: response.data || 'no data',
        processedDates: response.processed_dates || 'no processed dates info'
      });
      
      // Log API event for scheduling shift
      const employeeCount = (assignedEmployees?.length || 0) + 
        (assignedEmployeesToZones ? Object.values(assignedEmployeesToZones).flat().length : 0);
      const dateInfo = datePickerData.mode === 'single' 
        ? datePickerData.singleDate?.toISOString().split('T')[0]
        : `${datePickerData.startDate?.toISOString().split('T')[0]} to ${datePickerData.endDate?.toISOString().split('T')[0]}`;
      
      loggingService.logInfo(
        'shift_schedule',
        `User ${isUpdateOperation ? 'updated' : 'scheduled'} shift for site ${selectedSite.site_id}`,
        {
          endpoint: '/shift/roster',
          method: 'POST',
          siteId: selectedSite.site_id,
          date: dateInfo,
          dateMode: datePickerData.mode,
          shiftIndex: selectedShiftIndex + 1,
          shiftStartTime: currentShift.shift_start_time,
          shiftEndTime: currentShift.shift_end_time,
          employeeCount: employeeCount,
          operationType: isUpdateOperation ? 'update' : 'create',
          processedDates: response.processed_dates || (datePickerData.mode === 'range' ? 
            Math.ceil((new Date(datePickerData.endDate) - new Date(datePickerData.startDate)) / (1000 * 60 * 60 * 24)) + 1 : 1),
        }
      ).catch(logError => {
        console.warn('[ScheduleShift] Failed to log event:', logError);
      });
      
      setScheduleSuccess(true);
      
      // Show success message with more details
      // Enhanced success message to show date range information
      const dateRangeInfo = datePickerData.mode === 'range' 
        ? `Date Range: ${datePickerData.startDate?.toLocaleDateString()} - ${datePickerData.endDate?.toLocaleDateString()}`
        : `Date: ${datePickerData.singleDate?.toLocaleDateString()}`;
      
      const processedDatesCount = response.processed_dates || (datePickerData.mode === 'range' ? 
        Math.ceil((new Date(datePickerData.endDate) - new Date(datePickerData.startDate)) / (1000 * 60 * 60 * 24)) + 1 : 1);
      
      const operationType = isUpdateOperation ? 'updated' : 'scheduled';
      
      alert(`Shift ${operationType} successfully!\n\n` +
        `Site: ${selectedSite.site_name}\n` +
        `${dateRangeInfo}\n` +
        `Time: ${currentShift.shift_start_time} - ${currentShift.shift_end_time}\n` +
        `Employees Assigned: ${employeeCount}\n` +
        `${datePickerData.mode === 'range' ? `\n✅ Successfully ${operationType} for ${processedDatesCount} dates in the range.` : ''}`);
      
      // Update shift as saved (mark as not new)
      setShifts(prevShifts => prevShifts.map((shift, index) => {
        if (index === selectedShiftIndex) {
          return {
            ...shift,
            isNew: false,
            ss_id: response.ss_id || shift.ss_id // Update with new ss_id if provided
          };
        }
        return shift;
      }));
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setScheduleSuccess(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error scheduling shift:', error);
      setScheduleError(error.message);
      alert(`Error scheduling shift: ${error.message}`);
    } finally {
      setIsScheduling(false);
    }
  };

  // Zone assignment handlers are now handled by ZoneAssignmentManager component

  return (
    <div className="max-w max-h">
      

      {/* Selected Site Information */}
      <div className="bg-blue-50 rounded-xl p-6">
        
        {/* Instructions Text with Custom h3 Typography */}
      <div className="mb-2">
        <h5 className={typography.h5} style={{color: '#000000'}}>
          Please search and select the site to assign a shift
        </h5>
      </div>
        
        {/* Search Bar Section - Moved inside Selected Site Information */}
        <div className="mb-4">
          <div className="flex items-center gap-4 search-container">
            <div className="flex-1 relative">
              <div onClick={handleSearchBarClick}>
                <SearchBar
                  placeholder={isLoading ? "Loading sites..." : "Search by site name"}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSearch={handleSearchSubmit}
                  suggestions={searchSuggestions}
                  onSuggestionClick={handleSuggestionClick}
                  className="w-full max-w-2xl ml-0 cursor-pointer" 
                  disabled={isLoading}
                />
              </div>
              {/* Custom dropdown for all sites */}
              {showAllSitesDropdown && allSites.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm font-medium text-gray-700">
                      {searchTerm ? `Filtered results (${searchSuggestions.length})` : `All Sites (${allSites.length})`}
                    </p>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {searchSuggestions.map((siteName, index) => (
                      <div
                        key={index}
                        className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        onClick={() => handleSuggestionClick(siteName)}
                      >
                        <div className="font-medium text-gray-900">{siteName}</div>
                        {allSites.find(s => s.site_name === siteName)?.address && (
                          <div className="text-sm text-gray-500 truncate">
                            {allSites.find(s => s.site_name === siteName)?.address}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Site Details */}
        <div className="flex justify-between items-start mb-4">
          <h5 className={typography.h5} style={{color: '#000000', marginBottom: '0.24rem'}}>
            {selectedSite ? `Selected Site: ${selectedSite.site_name}` : 'Selected Site:'}
          </h5>
        </div>
        {selectedSite ? (
          <div className="space-y-2">
            {/* <p className={typography.p} style={{color: '#000000'}}>
              <span className="font-semibold">Site ID:</span> {selectedSite.site_id}
            </p> */}
            <p className={typography.p} style={{color: '#000000'}}>
              <span className="font-semibold">Address:</span> {selectedSite.address}
            </p>
          </div>
        ) : (
          <p className={typography.p} style={{color: '#000000'}}>
            Please search and select a site above
          </p>
        )}
      </div>

      {/* Pick date or date range */}
      <div className="mt-8 bg-blue-50 rounded-xl p-6">
        <h5 className={typography.h5} style={{color: '#000000', marginBottom: '0.24rem'}}>
          Pick date or Date range to assign shift
        </h5>
        {/* Date Range Button */}
        <div className="mt-4">
          <Card.Light className="w-full md:w-96 p-3">
            <button 
              className={`w-full inline-flex justify-between items-center transition-opacity duration-200 ${
                selectedSite 
                  ? 'hover:opacity-80 cursor-pointer' 
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => {
                if (selectedSite) {
                  openOverlay('datePicker');
                }
              }}
              disabled={!selectedSite}
            >
              <div className={`text-right justify-start text-lg font-normal ${typography.p} leading-relaxed ${
                selectedSite ? 'text-black' : 'text-gray-400'
              }`}>
                {selectedSite ? formatDateDisplay() : 'Please select a site first'}
              </div>
              <div className="w-6 h-6 relative overflow-hidden">
                {/* Calendar Icon */}
                <svg 
                  className={`w-5 h-5 absolute left-[2px] top-[2px] ${
                    selectedSite ? 'text-black' : 'text-gray-400'
                  }`}
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
                </svg>
              </div>
            </button>
          </Card.Light>
        </div>
        
        {/* Past Date Warning */}
        {(() => {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (datePickerData.mode === 'single' && datePickerData.singleDate) {
            const selectedDate = new Date(datePickerData.singleDate);
            selectedDate.setHours(0, 0, 0, 0);
            if (selectedDate < today) {
              return (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-amber-800">
                      <p className="font-medium text-sm">
                        Past Date Selected
                      </p>
                      <p className="text-xs mt-1">
                        You cannot schedule shifts for previous dates. Please select today or a future date.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
          } else if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
            const startDate = new Date(datePickerData.startDate);
            const endDate = new Date(datePickerData.endDate);
            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);
            
            if (startDate < today || endDate < today) {
              return (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-amber-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="text-amber-800">
                      <p className="font-medium text-sm">
                        Past Date Selected
                      </p>
                      <p className="text-xs mt-1">
                        You cannot schedule shifts for previous dates. Please select today or a future date for both start and end dates.
                      </p>
                    </div>
                  </div>
                </div>
              );
            } else if (endDate < startDate) {
              return (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-red-800">
                      <p className="font-medium text-sm">
                        Invalid Date Range
                      </p>
                      <p className="text-xs mt-1">
                        End date cannot be before start date. Please select a valid date range.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
          }
          return null;
        })()}
      </div>

      {/* Shifts if selected site || Shift Selection to Assign employees*/}
      {selectedSite && (datePickerData.mode === 'single' || datePickerData.mode === 'range') && (
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-6 w-full mt-8 overflow-x-hidden">
          {/* Loading State */}
          {isLoadingShifts && (
            <div className="w-full p-4 bg-blue-50 rounded-xl">
              <p className={typography.p} style={{color: '#000000'}}>
                Loading shifts for {selectedSite.site_name}...
              </p>
            </div>
          )}

          {/* Error State */}
          {shiftsError && (
            <div className="w-full p-4 bg-red-50 rounded-xl">
              <p className={typography.p} style={{color: '#dc2626'}}>
                {shiftsError}
              </p>
            </div>
          )}

          {/* Shift Tabs */}
          {!isLoadingShifts && (
            <div className="self-stretch h-14 inline-flex justify-start items-center gap-8 w-full">
              <div className="flex-1 flex justify-start items-center gap-6">
                {shifts.length > 0 ? (
                  shifts.map((shift, index) => (
                    <div
                      key={shift.ss_id}
                      className={`w-40 p-3 rounded-xl flex justify-between items-center cursor-pointer transition-all duration-200 ${
                        selectedShiftIndex === index
                          ? 'bg-gradient-to-bl from-stone-400 to-lime-600 shadow-[0px_4px_12px_0px_rgba(34,139,34,0.10)] outline outline-2 outline-offset-[-2px] outline-green-200/10'
                          : 'bg-neutral-50/50 hover:bg-neutral-100'
                      }`}
                      onClick={() => handleSelectShift(index)}
                    >
                      <p 
                        className={typography.p} 
                        style={{
                          color: selectedShiftIndex === index ? '#FFFFFF' : '#000000',
                          fontWeight: selectedShiftIndex === index ? 'bold' : 'normal'
                        }}
                      >
                        Shift - {shift.shift_index}
                      </p>
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveShift(index);
                        }}
                        icon={
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path 
                              d="M18 6L6 18M6 6L18 18" 
                              stroke={selectedShiftIndex === index ? "white" : "black"} 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        }
                      />
                    </div>
                  ))
                ) : (
                  <div className="w-40 p-3 bg-gradient-to-bl from-stone-400 to-lime-600 rounded-xl shadow-[0px_4px_12px_0px_rgba(34,139,34,0.10)] outline outline-2 outline-offset-[-2px] outline-green-200/10 flex justify-between items-center">
                    <p className={typography.p} style={{color: '#FFFFFF', fontWeight: 'bold'}}>
                      Shift - 1
                    </p>
                    <IconButton 
                      onClick={() => handleRemoveShift(0)}
                      icon={
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      }
                    />
                  </div>
                )}
              </div>
              
              {/* Add Shift Button */}
              <IconTextButton 
                icon={
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4V20M4 12H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
                text="Add Shift"
                onClick={isCurrentShiftComplete() ? handleAddShift : () => {
                  // Show warning message like in Flutter
                  const currentShift = shifts[selectedShiftIndex];
                  let message = "You cannot create a new shift until you fill and save the existing shift completely.\n\n";
                  
                  const hasTimeSet = (currentShift?.shift_start_time && currentShift.shift_start_time.trim() !== '') &&
                                   (currentShift?.shift_end_time && currentShift.shift_end_time.trim() !== '');
                  const hasSupervisors = assignedEmployees && assignedEmployees.length > 0;
                  const hasGuards = assignedEmployeesToZones && assignedEmployeesToZones.length > 0;
                  const hasBeenSaved = currentShift?.ss_id && currentShift.ss_id.toString().trim() !== '' && !currentShift.ss_id.startsWith('default_shift_');
                  
                  if (!hasTimeSet) {
                    message += "• Set the shift start and end times\n";
                  }
                  if (!hasSupervisors && !hasGuards) {
                    message += "• Add at least one supervisor or guard\n";
                  }
                  if (!hasBeenSaved) {
                    message += "• Click 'Schedule Shift' to save the shift\n";
                  }
                  
                  alert(message);
                }}
                disabled={!isCurrentShiftComplete()}
              />
            </div>
          )}

          {/* Displaying the shift details for the selected shift */}
          {!isLoadingShifts && (
            <div className="self-stretch flex flex-col justify-start items-start gap-6">
              <div className="self-stretch md:h-24 relative">
                <h3 className={typography.h3} style={{color: '#000000', marginBottom: '0.75rem'}}>
                  {shifts.length > 0 
                    ? `Shift - ${shifts[selectedShiftIndex]?.shift_index || 1}, Please select the time range`
                    : 'Shift - 1, Please select the time range'
                  }
                </h3>
                {/* Time Range */}
                <div className="w-full md:w-96 h-12 md:absolute md:left-0 md:top-[41px] rounded-xl inline-flex justify-between items-center gap-2 md:gap-0 overflow-hidden">
                  <div 
                    className="flex-1 md:w-44 self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex justify-between items-start overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openOverlay('startTimePicker')}
                  >
                    <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {shifts.length > 0 && shifts[selectedShiftIndex]?.shift_start_time 
                        ? shifts[selectedShiftIndex].shift_start_time
                        : formatTimeDisplay(startTimeData)
                      }
                    </div>
                    <div className="w-6 h-6 relative overflow-hidden flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6v6l4 2M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className={typography.h3} style={{color: '#000000'}}>
                    ~
                  </h3>
                  <div 
                    className="flex-1 md:w-44 self-stretch p-3 bg-white rounded-xl outline outline-1 outline-offset-[-1px] outline-zinc-300 flex justify-between items-start overflow-hidden cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => openOverlay('endTimePicker')}
                  >
                    <div className="justify-start text-black text-lg font-normal font-['Open_Sans'] leading-relaxed">
                      {shifts.length > 0 && shifts[selectedShiftIndex]?.shift_end_time 
                        ? shifts[selectedShiftIndex].shift_end_time
                        : formatTimeDisplay(endTimeData)
                      }
                    </div>
                    <div className="w-6 h-6 relative overflow-hidden flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 6v6l4 2M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Overnight Shift Indicator */}
              {overnightShiftInfo.isOvernight && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-10 h-10 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                    <div className="text-blue-800">
                      <sh3 className={typography.sh2}>
                        Night Shift
                      </sh3>
                      <p className={`${typography.p} mt-1`}>
                        From {overnightShiftInfo.startTimeFormatted} to {overnightShiftInfo.endTimeFormatted} is considered as overnight shift
                      </p>
                      {datePickerData.mode === 'range' && (
                        <p className={`${typography.p} mt-1 font-medium`}>
                          This shift will span across multiple days in the selected date range
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Time Validation Error */}
              {!overnightShiftInfo.isValid && overnightShiftInfo.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="text-red-800">
                      <p className="font-medium text-sm">
                        Invalid Time Range
                      </p>
                      <p className="text-xs mt-1">
                        {overnightShiftInfo.error}
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Assign Employees Section */}
              <AssignEmployeeTable
                assignedEmployees={assignedEmployees}
                onAddEmployee={handleOpenAssignEmployees}
                onRemoveEmployee={handleRemoveEmployee}
                onDeleteEmployee={handleDeleteEmployee}
                onNoteChange={handleEmployeeNoteChange}
              />



              {/* Assign Employees to Zones Section */}
              {selectedSite && (
                <ZoneAssignmentManager
                  siteId={selectedSite.site_id}
                  zoneGuards={assignedEmployeesToZones || {}}
                  employeeType="guards"
                  onZoneGuardsChange={setAssignedEmployeesToZones}
                  shiftValidationData={{
                    selectedSite: selectedSite,
                    currentShift: shifts[selectedShiftIndex],
                    date: datePickerData.mode === 'single' 
                      ? datePickerData.singleDate?.toISOString().split('T')[0]
                      : datePickerData.startDate?.toISOString().split('T')[0],
                    startTime: shifts[selectedShiftIndex]?.shift_start_time || formatTimeDisplay(startTimeData),
                    endTime: shifts[selectedShiftIndex]?.shift_end_time || formatTimeDisplay(endTimeData)
                  }}
                />
              )}
            </div>
          )}

          {/* Schedule Status Messages */}
          {scheduleSuccess && (
            <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
              ✅ Shift scheduled successfully!
            </div>
          )}
          
          {scheduleError && (
            <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              ❌ Error: {scheduleError}
            </div>
          )}

          {/* Shift Summary */}
          {shifts.length > 0 && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className={typography.h5} style={{color: '#000000', marginBottom: '0.5rem'}}>
                Shift Summary
              </h4>
              <div className="space-y-2 text-sm">
                <p><strong>Site:</strong> {selectedSite?.site_name}</p>
                <p><strong>Date:</strong> {
                  datePickerData.mode === 'single' 
                    ? datePickerData.singleDate?.toLocaleDateString()
                    : `${datePickerData.startDate?.toLocaleDateString()} - ${datePickerData.endDate?.toLocaleDateString()}`
                }</p>
                <p><strong>Time:</strong> {shifts[selectedShiftIndex]?.shift_start_time} - {shifts[selectedShiftIndex]?.shift_end_time}</p>
                <p><strong>Duration:</strong> {getCurrentShiftDuration().toFixed(1)} hours</p>
                <p><strong>Higher Employee:</strong> {assignedEmployees?.length || 0}</p>
                <p><strong>Employee:</strong> {assignedEmployeesToZones ? Object.values(assignedEmployeesToZones).flat().length : 0}</p>
                <p><strong>Total Employees:</strong> {(assignedEmployees?.length || 0) + (assignedEmployeesToZones ? Object.values(assignedEmployeesToZones).flat().length : 0)}</p>
              </div>
            </div>
          )}

          {/* Primary Button */}
        <div className="mt-6 w-full overflow-x-hidden">
          <PrimaryButton 
            onClick={handleScheduleShift}
            disabled={isScheduling || !selectedSite || shifts.length === 0 || 
              (!assignedEmployees?.length && (!assignedEmployeesToZones || Object.keys(assignedEmployeesToZones).length === 0)) ||
              (() => {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                if (datePickerData.mode === 'single' && datePickerData.singleDate) {
                  const selectedDate = new Date(datePickerData.singleDate);
                  selectedDate.setHours(0, 0, 0, 0);
                  return selectedDate < today;
                } else if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
                  const startDate = new Date(datePickerData.startDate);
                  const endDate = new Date(datePickerData.endDate);
                  startDate.setHours(0, 0, 0, 0);
                  endDate.setHours(0, 0, 0, 0);
                  return startDate < today || endDate < today || endDate < startDate;
                }
                return false;
              })()}
            className="w-full max-w-full"
          >
            {isScheduling ? 'Scheduling...' : 'Schedule Shift'}
          </PrimaryButton>
          
          {/* Help text */}
          {(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            let isPastDate = false;
            let isInvalidRange = false;
            
            if (datePickerData.mode === 'single' && datePickerData.singleDate) {
              const selectedDate = new Date(datePickerData.singleDate);
              selectedDate.setHours(0, 0, 0, 0);
              isPastDate = selectedDate < today;
            } else if (datePickerData.mode === 'range' && datePickerData.startDate && datePickerData.endDate) {
              const startDate = new Date(datePickerData.startDate);
              const endDate = new Date(datePickerData.endDate);
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(0, 0, 0, 0);
              isPastDate = startDate < today || endDate < today;
              isInvalidRange = endDate < startDate;
            }
            
            const hasNoEmployees = !assignedEmployees?.length && (!assignedEmployeesToZones || Object.keys(assignedEmployeesToZones).length === 0);
            
            if (isInvalidRange) {
              return (
                <p className="mt-2 text-sm text-red-600">
                  ❌ End date cannot be before start date. Please select a valid date range.
                </p>
              );
            } else if (isPastDate) {
              return (
                <p className="mt-2 text-sm text-red-600">
                  ❌ Cannot schedule shifts for previous dates. Please select today or a future date.
                </p>
              );
            } else if (hasNoEmployees) {
              return (
                <p className="mt-2 text-sm text-amber-600">
                  ⚠️ Please assign at least one employee before scheduling the shift
                </p>
              );
            }
            return null;
          })()}
        </div>
        </div>
      )}
      

      

            {/* AssignEmployees Dialog */}
      {/* This component is now managed by OverlayContext */}

      {/* DatePicker Component */}
      {/* This component is now managed by OverlayContext */}

      {/* TimePicker Components */}
      {/* These components are now managed by OverlayContext */}
      
    </div>
  );
};

export default ScheduleShift;